<template>
  <ion-page>
    <ion-content>
      <main class="flex">
        <Logo v-if="isLogin || isLoading"/>
        <div class="form" v-if="isLogin">
          <ion-label class="ion-text-center ion-margin-bottom"><h1>{{ "'24 Wrapped" }}</h1></ion-label>
          <ion-label class="ion-text-center ion-margin-bottom"><p>{{ "Every effort, every idea, every win — big or small — has contributed to something greater. It’s time to look back and see the impact you’ve created this year." }}</p></ion-label>

          <ion-item lines="none" class="ion-margin-bottom">
            <ion-input :error-text="errorInput" label="Email" label-placement="fixed" name="email" v-model="email" id="email" type="email" required @keyup.enter="fetchData"/>
          </ion-item>

          <ion-button expand="block" @click="fetchData" :disabled="!email">
            Unwrap my year
            <ion-icon slot="end" :icon="giftOutline"/>
          </ion-button>

          <img src="../assets/images/footerIcons.svg" alt="" class="platform-icons">
        </div>

        <div class="loading" v-else-if="isLoading">
          <div
            class="profile-pic"
            v-motion
            :initial="{
              opacity: 0,
              y: 100
            }"
            :visible="{
              opacity: 1,
              y: 0,
              transition: {
                duration: 300,
                type: 'keyframes',
                ease: 'easeIn',
              }
            }"
          >
            <img v-if="currentUser.headshot" :src="`assets/images/headShots/${currentUser.headshot}.png`" />
            <img v-else src="@/assets/images/defaultImage.png" />
          </div>
          <h1 class="ion-text-center">{{ currentUser.firstName }} {{ currentUser.middleName }} {{ currentUser.lastName }}</h1>
          <h3 class="ion-text-center">You've had a big year. Lets break it down!</h3>

          <ion-progress-bar :color="loading <= 0.3 ? 'primary' : loading > 0.3 && loading <= 0.5 ? 'secondary' : loading > 0.5 && loading <= 0.7 ? 'tertiary' : 'success'" :value="loading" class="ion-margin-bottom"></ion-progress-bar>
          <ion-label class="ion-text-center ion-margin-bottom">
            <p>
              {{ loading <= 0.3 ? "Loading your year..." : loading > 0.3 && loading <= 0.5 ? "Counting your contributions" : loading > 0.5 && loading <= 0.7 ? "Pulling in your prodest moments..." : "Almost there..." }}
            </p>
          </ion-label>

          <ion-label class="ion-text-center">
            <h4>
              {{ "Your year was full of wins, teamwork, and growth. Let's see everything you made happen." }}
            </h4>
          </ion-label>
        </div>

        <div class="wrapped" v-else>
          <section class="user-info">
            <Logo />
            <div
              class="profile-pic"
              v-motion
              :initial="{
                opacity: 0,
                y: 100
              }"
              :visible="{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 300,
                  type: 'keyframes',
                  ease: 'easeIn',
                }
              }"
            >
              <img v-if="currentUser.headshot" :src="`assets/images/headShots/${currentUser.headshot}.png`" />
              <img v-else src="@/assets/images/defaultImage.png" />
            </div>
            <h1 class="ion-text-center">{{ currentUser.firstName }} {{ currentUser.middleName }} {{ currentUser.lastName }}</h1>
            <h3 class="ion-text-center">Let's see your 2024 story.</h3>

            <ion-fab ref="scrollDown" class="ion-margin-top" horizontal="center" @click="scrollToElement">
              <ion-fab-button>
                <ion-icon :icon="arrowDownOutline" />
              </ion-fab-button>
            </ion-fab>
          </section>

          <!-- GitHub -->
          <div id="gitHub" class="info" v-if="currentUser?.ghTotalPrs || currentUser?.ghTopRepo || currentUser?.ghFastestPr || currentUser?.ghTotalContributions || currentUser?.ghMerges">
            <h1 class="title ion-margin-start">GitHub</h1>

            <DataCard
              v-if="currentUser?.ghTotalPrs"
              sub-title="Pull requests"
              :icon="logoGithub"
              :value="currentUser?.ghTotalPrs"
              additional-info="Pull requests created by you were merged on GitHub"
            />

            <ListCard
              v-if="currentUser?.ghTopRepo"
              sub-title="Top repositories"
              :value="currentUser?.ghTopRepo"
              suffix="commit"
            />

            <BarChartCard
              v-if="currentUser?.ghFastestPr"
              sub-title="Fastest PR"
              :data="currentUser?.ghFastestPr"
            />

            <DataCard
              v-if="currentUser?.ghTotalContributions"
              sub-title="Contributions"
              :icon="logoGithub"
              :value="currentUser?.ghTotalContributions"
              additional-info="Across all repositories"
            />

            <DataCard
              v-if="currentUser?.ghMerges"
              sub-title="Owning the commit"
              :icon="gitPullRequestOutline"
              :value="currentUser?.ghMerges"
              additional-info="Pull requests merged by you this year on GitHub"
            />
          </div>

          <!-- GitLab -->
          <div id="gitLab" class="info" v-if="currentUser?.glTotalMrs || currentUser?.glTopRepo || currentUser?.glMerges || currentUser?.glFvB">
            <h1 class="title ion-margin-start">GitLab</h1>

            <DataCard
              v-if="currentUser?.glTotalMrs"
              sub-title="Merge requests"
              :icon="logoGitlab"
              :value="currentUser?.glTotalMrs"
              additional-info="Pull requests created by you were merged on GitLab"
            />

            <ListCard
              v-if="currentUser?.glTopRepo"
              sub-title="Top repositories"
              :value="currentUser?.glTopRepo"
              suffix="commit"
            />

            <DataCard
              v-if="currentUser?.glMerges"
              sub-title="Owning the commit"
              :icon="gitPullRequestOutline"
              :value="currentUser?.glMerges"
              additional-info="Pull requests merged by you this year on GitLab"
            />

            <DoughnutChartCard
              v-if="currentUser?.glFvB"
              sub-title="Feature vs Bug"
              :icon="bugOutline"
              :data="currentUser?.glFvB"
              additional-info="Bug fix merge requests"
            />
          </div>

          <!-- ClickUp -->
          <div id="clickUp" class="info">
            <h1 class="title ion-margin-start">ClickUp</h1>

            <DataCard
              v-if="currentUser?.cuTickets"
              sub-title="Tickets"
              :icon="ticketOutline"
              :value="currentUser?.cuTickets"
              additional-info="Created this year by you"
            />

            <DataCard
              sub-title="Closed"
              :icon="checkmarkDone"
              value="1492"
              additional-info="Tickets this year as a team"
            />
          </div>

          <!-- SuperSet -->
          <div id="superSet" class="info" v-if="currentUser?.ssDataSets || currentUser?.ssCharts || currentUser?.ssDashboards || currentUser?.ssClients">
            <h1 class="title ion-margin-start">SuperSet</h1>

            <DataCard
              v-if="currentUser?.ssDataSets"
              sub-title="Data Sets"
              :icon="layersOutline"
              :value="currentUser?.ssDataSets"
              additional-info="Created and SQLs added"
            />

            <DataCard
              v-if="currentUser?.ssCharts"
              sub-title="Reports & Alerts"
              :icon="fitnessOutline"
              :value="currentUser?.ssCharts"
              additional-info="Created this year by you"
            />

            <DataCard
              v-if="currentUser?.ssDashboards"
              sub-title="Dashboards"
              :icon="speedometerOutline"
              :value="currentUser?.ssDashboards"
              additional-info="Managed by you"
            />

            <ListCard
              v-if="currentUser?.ssClients"
              sub-title="Clients"
              :value="currentUser?.ssClients"
              suffix="chart"
            />
          </div>

          <!-- NiFi -->
          <div id="nifi" class="info" v-if="currentUser?.nfProcessors || currentUser?.nfTopProcessors || currentUser?.nfFlow">
            <h1 class="title ion-margin-start">NiFi</h1>

            <DataCard
              v-if="currentUser?.nfProcessors"
              sub-title="Processors"
              :icon="shuffleOutline"
              :value="currentUser?.nfProcessors"
              additional-info="Updated by you"
            />

            <ListCard
              v-if="currentUser?.nfTopProcessors"
              sub-title="Favorite processors"
              :value="currentUser?.nfTopProcessors"
            />

            <DataCard
              v-if="currentUser?.nfFlow"
              sub-title="Flows"
              :icon="createOutline"
              :value="currentUser?.nfFlow"
              additional-info="Created by you this year"
            />
          </div>

          <!-- Slack -->
          <div id="slack" class="info">
            <h1 class="title ion-margin-start">Slack</h1>

            <DataCard
              v-if="currentUser?.slDays"
              sub-title="Days active"
              :icon="logoSlack"
              :value="currentUser?.slDays"
              additional-info="+ Active days this year"
            />

            <DataCard
              v-if="currentUser?.slMessages"
              sub-title="Messages"
              :icon="mailOpenOutline"
              :value="currentUser?.slMessages"
              additional-info="Sent by you this year"
            />

            <DataCard
              sub-title="Support"
              :icon="headsetOutline"
              value="266"
              additional-info="HotWax Support active days this year"
            />

            <DataCard
              sub-title="Support"
              :icon="chatbubbleEllipsesOutline"
              value="1622"
              additional-info="Messages sent by HotWax Support this year"
            />
          </div>

          <!-- Meetings -->
          <div id="Meetings" class="info" v-if="currentUser?.gmMeetings || currentUser?.gmClients || currentUser?.gmHours || currentUser?.gmMinutes">
            <h1 class="title ion-margin-start">Meetings</h1>

            <DataCard
              v-if="currentUser?.gmMeetings"
              sub-title="Meetings"
              :icon="calendarOutline"
              :value="currentUser?.gmMeetings"
              additional-info="Taken this year"
            />

            <ListCard
              v-if="currentUser?.gmClients"
              sub-title="Clients"
              :icon="rocketOutline"
              :value="currentUser?.gmClients"
            />
            <DataCard
              v-if="currentUser?.gmHours"
              sub-title="Hours"
              :icon="moonOutline"
              :value="currentUser?.gmHours"
              additional-info="Hours invested by you"
            />
            <DataCard
              v-if="currentUser?.gmMinutes"
              sub-title="Minutes"
              :icon="timerOutline"
              :value="currentUser?.gmMinutes"
              additional-info="Minutes invested by you"
            />

          </div>

          <!-- Figma -->
          <div id="figma" class="info" v-if="currentUser?.fgReleaseNotes || currentUser?.fgWebsite || currentUser?.fgDocumentation || currentUser?.fgEvent">
            <h1 class="title ion-margin-start">Figma</h1>

            <DataCard
              v-if="currentUser?.fgReleaseNotes"
              sub-title="Release Notes"
              :icon="logoFigma"
              :value="currentUser?.fgReleaseNotes"
              additional-info="Images created this year by you"
            />

            <DataCard
              v-if="currentUser?.fgWebsite"
              sub-title="Website"
              :icon="checkmarkDoneOutline"
              :value="currentUser?.fgWebsite"
              additional-info="Images created this year by you"
            />

            <DataCard
              v-if="currentUser?.fgDocumentation"
              sub-title="Documentation"
              :icon="checkmarkDoneOutline"
              :value="currentUser?.fgDocumentation"
              additional-info="Images created this year by you"
            />

            <DataCard
              v-if="currentUser?.fgEvent"
              sub-title="Event"
              :icon="checkmarkDoneOutline"
              :value="currentUser?.fgEvent"
              additional-info="Images created this year by you"
            />
          </div>
          <aside class="center-block">
            <h2>Thats a wrap for 2024!</h2>
            <ion-label>
              Thank you for an incredible year {{ currentUser?.firstName }} {{ currentUser?.middleName }} {{ currentUser?.lastName }}.
              <br>
              <br>
              <p>
                Every effort, every idea, every contribution you made this year is part of something greater. Together, we built solutions, solved challenges, and supported each other as a team. Thank you for being part of the HotWax family.
              </p>
            </ion-label>
          </aside>
          <div id="credits" class="center-block">
            <hr>
            <ion-label>
              A special shoutout to the people who brought this to life.
              <br>
              <br>
              Built with ♥️ by Ajinkya, Harshita, Param, Parnika, Priyanshee, Shriya & Vidhan
            </ion-label>

            <div class="buttons">
              <ion-button class="ion-margin-top" fill="clear" @click="redirect()">Give feedback!</ion-button>
              <ion-button class="ion-margin-top" fill="clear" @click="replay()">Replay!</ion-button>
            </div>
          </div>
        </div>
      </main>
    </ion-content>    
  </ion-page>
</template>

<script setup lang="ts">
import { alertController, IonButton, IonContent, IonFab, IonFabButton, IonIcon, IonInput, IonItem, IonLabel, IonPage, IonProgressBar } from "@ionic/vue";
import Logo from "@/components/Logo.vue";
import { arrowDownOutline, bugOutline, calendarOutline, chatbubbleEllipsesOutline, checkmarkDone, checkmarkDoneOutline, createOutline, fitnessOutline, giftOutline, gitPullRequestOutline, headsetOutline, layersOutline, logoFigma, logoGithub, logoGitlab, logoSlack, mailOpenOutline, moonOutline, rocketOutline, shuffleOutline, speedometerOutline, ticketOutline, timerOutline } from "ionicons/icons"
import { onMounted, ref, watch } from "vue";
import DataCard from "@/components/DataCard.vue"
import ListCard from "@/components/ListCard.vue"
import BarChartCard from "@/components/BarChartCard.vue"
import { Chart as ChartJS, ArcElement, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import DoughnutChartCard from "@/components/DoughnutChartCard.vue";
import { useMotion } from "@vueuse/motion";

ChartJS.register(ArcElement, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)
const confetti = require('canvas-confetti');

const email = ref("")
const isLogin = ref(true)
let loading = ref(0)
let isLoading = ref(false)
let data = ref({}) as any
let primaryColor = ref('')
let currentUser = ref({}) as any
let errorInput = ref("")
const scrollDown = ref()

var style = window.getComputedStyle(document.body)

const { variant } = useMotion(scrollDown, {
  initial: {
    y: 100,
    opacity: 0,
  },
  enter: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 350,
      damping: 20,
      delay: 50,
      onComplete: () => {
        variant.value = "levitate";
      },
    },
  },
  levitate: {
    y: 15,
    transition: {
      duration: 2000,
      repeat: Infinity,
      ease: "easeInOut",
      repeatType: "mirror",
    },
  },
});

onMounted(() => {
  primaryColor.value = style.getPropertyValue('--ion-color-primary')

  try {
    data.value = require("../assets/json/users.json")
  } catch(err) {
    console.log('error', err);
  }

  let canvas = document.getElementById('card') as any;
  if(canvas) {
    canvas.confetti = canvas.confetti || confetti.create(canvas, { resize: true });
    canvas.confetti({
      spread: 70,
      particleCount: 100,
      origin: { y: 1.2 }
    });
  }
})

watch(email, () => {
  errorInput.value = ""
})

function scrollToElement() {
  let elementId = "info"

  const el = document.getElementsByClassName(elementId);
  if (el.length) el[0].scrollIntoView({ behavior: 'smooth' });
}

function redirect() {
  window.open("https://forms.gle/hrZkyCqVYBhxYFgF7", "_blank")
}

function replay() {
  email.value = ""
  isLogin.value = true
  loading.value = 0
  isLoading.value = false
  currentUser.value = {}
  errorInput.value = ""
}

async function presentAlert(message: string, header?: string) {
  const alert = await alertController.create({
    header,
    message,
    buttons: [
      {
        text: "Give feedback!",
        handler: () => {
          redirect()
        }
      },
      {
        text: "Okay!",
      }
    ],
  });
  return alert.present();
}

async function fetchData() {
  if(!(email.value && (email.value.includes("@hotwax.co") || email.value.includes("@hotwaxsystems.com")))) {
    errorInput.value = "Enter valid email"
    return;
  }

  const user = email.value.split("@")[0];

  if(!user) {
    errorInput.value = "Enter valid email"
    return;
  }

  currentUser.value = data.value[user];

  if(!currentUser.value || !Object.keys(currentUser.value)?.length) {
    presentAlert("You have done some amazing work this year 🎉, but due to some technical limitations we are unable to fetch your data.<br/ ><br/ >Please check this project again next year.<br/> <br/>Thank You and Happy New Year. 🥳")
    return;
  }
  
  if(currentUser.value) {
    isLogin.value = false
    isLoading.value = true

    const interval = setInterval(() => {
      loading.value += 0.01;

      if(loading.value > 1) {
        clearInterval(interval)
        isLoading.value = false
      }
    }, 50);
  }
}
</script>

<style>
main {
  height: 100vh;
  align-content: center;
}

.wrapped {
  padding-bottom: 80px;
}

figure, .form, .loading, .profile-pic {
  max-width: 375px;
  margin-inline: auto;
}

.platform-icons {
  margin-inline: auto;
  display: block;
  margin-block-start: 50px;
}

ion-button {
  --color: white;
}

.profile-pic {
  margin-inline: auto;
  position: relative;
  width: 250px;
  height: 250px;
  justify-self: center;
  clip-path: polygon(50% 0, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%);
  animation: 5s ease-in 0s 1 profile-background;
  animation-fill-mode: forwards;
}

.profile-pic > img {
  position: absolute;
  width: 232px;
  height: 232px;
  top: 9px;
  left: 9px;
  object-fit: cover;
  clip-path: polygon(50% 0, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%);
}

.user-info {
  height: 100vh;
  align-content: center;
}

ion-fab-button {
  --border-color: var(--ion-color-tertiary);
  --border-width: 2px;
  --border-style: solid;
  --background: transparent;
  --color: var(--ion-color-light);
}

.info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(375px, 1fr));
  max-width: 850px;
  margin-inline: auto;
}

.info h1 {
  grid-column: 1 / -1;
}

.confetti {
  left: 50%;
}

.center-block {
  max-width: 375px;
  margin-inline: auto;
  display: block;
  margin-block-start: 64px;
  text-align: center;
}

.center-block * {
  display: inherit;
}

#credits {
  color: var(--ion-color-medium);
}

#credits hr {
  border: .1px solid var(--ion-color-medium);
  margin-block-end: 32px;
  height: 0;
}

.buttons {
  display: flex;
  justify-content: space-between;
}

@media (min-width: 750px) {
  .center-block {
    max-width: 750px;
  }
}

.buttons {
  display: flex;
  justify-content: space-between;
}

@keyframes profile-background {
  from {
    background-color: transparent;
  }
  to {
    background-color: #f79521;
  }
}
</style>